import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from '../utils/localStorage';
import { api } from '../utils/apiClient';
import { config } from '../config';
import { TOKEN_PACKAGES } from '../constants/tokenPackages';

/**
 * User authentication and admin rights model:
 * 
 * 1. Admin Rights:
 *    - Admin rights are managed through an explicit 'userRole' flag in localStorage
 *    - Admin rights are only granted through:
 *      a) Password verification in Settings component
 *      b) Development mode admin password fallback
 *    - Any authenticated user can become an admin by verifying the admin password
 * 
 * 2. User Authentication:
 *    - Regular users authenticate by email and password
 *    - Auth tokens are stored in both localStorage and cookies for redundancy
 *    - Token validation is used to verify authentication but NOT to grant admin rights
 * 
 * 3. Security:
 *    - Admin rights are always cleared on logout
 *    - Invalid tokens never grant admin access
 *    - App enforces security checks at the component level
 */

// Helper function to get a cookie value by name
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

// Helper function to set a cookie
const setCookie = (name: string, value: string, days = 30): void => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
};

// Enum for user types
export enum UserRole {
  Public = 'public',
  User = 'user',
  Admin = 'admin'
}

// Type for user data
export interface UserData {
  email: string | null;
  role: UserRole;
  token: string | null;
  tokenBalance: number;
  countryCode?: string | null;
}

// Define transaction details interface
interface TransactionDetails {
  tokenAmount?: number;
  priceAmount?: number;
}

// Interface for context value
interface UserContextType {
  // User state
  user: UserData;
  isLoggedIn: boolean;
  isAdmin: boolean;
  isAuthenticating: boolean;
  isTokenOperationLoading: boolean;
  stripePaymentSuccess: boolean;
  setStripePaymentSuccess: (success: boolean) => void;
  
  // Auth methods
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  verifyToken: () => Promise<boolean>;
  
  // User methods
  signup: (email: string, password: string) => Promise<boolean>;
  updateEmail: (email: string, password: string) => Promise<boolean>;
  setAdminRole: (enable: boolean, password: string) => Promise<boolean>;
  setCountryCode: (countryCode: string | null) => void;
  
  // Password methods
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<boolean>;
  
  // Token methods
  addTokens: (amount: number) => Promise<boolean>;
  refreshTokenBalance: () => Promise<boolean>;
  
  // Access control helper
  hasCounterAccess: (ownerEmail: string | null, isOwnerUrl: boolean, accessType: 'view' | 'manage') => boolean;
  
  // Centralized payment success handler
  handlePaymentSuccess: (sessionId: string, details?: TransactionDetails) => Promise<boolean>;
}

// Create context
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider component
export function UserProvider({ children }: { children: ReactNode }) {
  // Initial user state
  const [user, setUser] = useState<UserData>(() => {
    // Get stored token
    const storedToken = getLocalStorageItem<string>('authToken', '') || getCookie('authToken') || null;
    // Get stored email
    const storedEmail = getLocalStorageItem<string>('userEmail', '') || getCookie('userEmail') || null;
    // Get stored role
    const storedRoleString = getLocalStorageItem<string>('userRole', '');
    
    // Only use stored role if it matches the current user's email
    const storedRole = storedEmail && storedRoleString === 'admin' ? UserRole.Admin : 
                       (storedEmail ? UserRole.User : UserRole.Public);
    
    return {
      email: storedEmail,
      role: storedRole,
      token: storedToken,
      tokenBalance: 0,
    };
  });
  
  // Loading states
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const [isTokenOperationLoading, setIsTokenOperationLoading] = useState<boolean>(false);
  
  // Add state for tracking Stripe payment success
  const [stripePaymentSuccess, setStripePaymentSuccess] = useState<boolean>(false);
  
  // Computed states
  const isLoggedIn = !!user.email && !!user.token;
  const isAdmin = user.role === UserRole.Admin;
  
  // Fetch token balance when email changes
  useEffect(() => {
    let isMounted = true;
    
    const fetchTokenBalance = async () => {
      if (!user.email || !user.token) {
        if (isMounted) setUser(prev => ({ ...prev, tokenBalance: 0 }));
        return;
      }

      try {
        setIsTokenOperationLoading(true);
        const response = await api.wallet.getTokenBalance();
        
        if (isMounted && response.success && response.tokenBalance !== undefined) {
          setUser(prev => ({ 
            ...prev, 
            tokenBalance: response.tokenBalance ?? 0 // Ensure we never assign undefined
          }));
        }
      } catch (error) {
        // Silent fail, keep previous balance
      } finally {
        if (isMounted) setIsTokenOperationLoading(false);
      }
    };

    fetchTokenBalance();
    
    return () => { isMounted = false; };
  }, [user.email, user.token]);
  
  // Methods for managing user state
  const updateUserState = (updates: Partial<UserData>) => {
    setUser(prev => ({ ...prev, ...updates }));
    
    // Update storage for persistent fields
    if (updates.email !== undefined) {
      if (updates.email) {
        setLocalStorageItem('userEmail', updates.email);
        setCookie('userEmail', updates.email);
      } else {
        removeLocalStorageItem('userEmail');
        document.cookie = 'userEmail=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    }
    
    if (updates.token !== undefined) {
      if (updates.token) {
        setLocalStorageItem('authToken', updates.token);
        setCookie('authToken', updates.token);
      } else {
        removeLocalStorageItem('authToken');
        document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    }
    
    if (updates.role !== undefined) {
      setLocalStorageItem('userRole', updates.role);
    }
  };
  
  // Verify if current token is valid
  const verifyToken = async (): Promise<boolean> => {
    if (!user.token || !user.email) return false;
    
    try {
      setIsAuthenticating(true);
      const response = await api.auth.verifyToken();
      
      if (response.success) {
        return true;
      } else {
        // If token is invalid, reset token but keep email
        updateUserState({
          token: null,
          role: UserRole.Public
        });
        return false;
      }
    } catch (error) {
      // On error, assume token is invalid
      updateUserState({
        token: null,
        role: UserRole.Public
      });
      return false;
    } finally {
      setIsAuthenticating(false);
    }
  };
  
  // Login (email and password)
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsAuthenticating(true);
      
      if (!email || !password) {
        return false;
      }
      
      // Regular user login
      const response = await api.auth.userLogin(email, password);
      
      if (response.success && response.token) {
        // Check if user had a preferred role saved
        const preferredRole = getLocalStorageItem<string>('preferredUserRole', '');
        const userRole = preferredRole === 'admin' ? UserRole.Admin : UserRole.User;
        
        updateUserState({
          email,
          token: response.token,
          role: userRole
        });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsAuthenticating(false);
    }
  };
  
  // User signup (email and password)
  const signup = async (email: string, password: string): Promise<boolean> => {
    if (!email || !email.includes('@') || !password) return false;
    
    try {
      setIsAuthenticating(true);
      const response = await api.auth.signup(email, password);
      
      if (response.success && response.token) {
        // Check if user had a preferred role saved
        const preferredRole = getLocalStorageItem<string>('preferredUserRole', '');
        const userRole = preferredRole === 'admin' ? UserRole.Admin : UserRole.User;
        
        // Update user state
        updateUserState({
          email,
          token: response.token,
          role: userRole
        });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Signup error:', error);
      return false;
    } finally {
      setIsAuthenticating(false);
    }
  };
  
  // Update user email with password verification
  const updateEmail = async (newEmail: string, password: string): Promise<boolean> => {
    if (!user.token || !user.email || !newEmail.includes('@') || !password) return false;
    
    try {
      const response = await api.auth.updateEmail(newEmail, password);
      
      if (response.success) {
        // For immediate UX feedback, update the local state, 
        // even though the actual email change requires verification
        // updateUserState({ email: newEmail });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Update email error:', error);
      return false;
    }
  };
  
  // Change password
  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (!user.token || !user.email || !currentPassword || !newPassword) return false;
    
    try {
      const response = await api.auth.changePassword(currentPassword, newPassword);
      return response.success;
    } catch (error) {
      console.error('Change password error:', error);
      return false;
    }
  };
  
  // Request password reset
  const requestPasswordReset = async (email: string): Promise<boolean> => {
    if (!email || !email.includes('@')) return false;
    
    try {
      const response = await api.auth.requestPasswordReset(email);
      return response.success;
    } catch (error) {
      console.error('Password reset request error:', error);
      return false;
    }
  };
  
  // Add tokens to wallet
  const addTokens = useCallback(async (amount: number): Promise<boolean> => {
    if (!user.email || !user.token || !amount || amount <= 0 || !Number.isInteger(amount)) {
      return false;
    }
    
    setIsTokenOperationLoading(true);
    
    try {
      const response = await api.wallet.addTokens(amount);
      
      if (response.success && response.tokenBalance !== undefined) {
        // Update user state with new balance
        updateUserState({ tokenBalance: response.tokenBalance });
        return true;
      }
      
      // Handle specific errors
      if (response.error === 'AUTH_REQUIRED') {
        const isValid = await verifyToken();
        if (!isValid) {
          // Token invalid, user needs to log in again
          return false;
        }
      }
      
      return false;
    } catch (error) {
      return false;
    } finally {
      setIsTokenOperationLoading(false);
    }
  }, [user.email, user.token]);
  
  // Refresh token balance from server
  const refreshTokenBalance = async (): Promise<boolean> => {
    if (!user.email || !user.token) {
      return false;
    }
    
    setIsTokenOperationLoading(true);
    
    try {
      const response = await api.wallet.getTokenBalance();
      
      if (response.success && response.tokenBalance !== undefined) {
        // Update user state with new balance
        updateUserState({ tokenBalance: response.tokenBalance });
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    } finally {
      setIsTokenOperationLoading(false);
    }
  };
  
  // Centralized payment success handler to avoid duplicate processing
  const handlePaymentSuccess = async (sessionId: string, details?: TransactionDetails): Promise<boolean> => {
    try {
      // First check if session is complete
      const result = await api.wallet.getSessionStatus(sessionId);
      
      // Store transaction details for later retrieval
      if (details) {
        const { getTransactionKey } = await import('../utils/paymentUtils');
        const transactionData = {
          sessionId,
          tokenAmount: details.tokenAmount,
          price: details.priceAmount,
          date: new Date().toISOString()
        };
        
        // Store in sessionStorage for primary access
        const storageKey = getTransactionKey(sessionId);
        sessionStorage.setItem(storageKey, JSON.stringify(transactionData));
        
        // Also store in localStorage as a backup
        try {
          localStorage.setItem(storageKey, JSON.stringify(transactionData));
        } catch (e) {
          // Silent fail for localStorage
        }
      }
      
      // Check if there was an error with the session
      if (!result.success) {
        if (result.error === 'SESSION_NOT_FOUND' || result.code === 'resource_missing') {
          // In case of Stripe account change, return false without showing errors
          return false;
        } else {
          // Log the error but don't show to user
          console.error('Error checking payment status:', result.message || result.error);
        }
        return false;
      }
      
      // Check both session status and payment_status
      // Only consider payment successful if status is 'complete' AND payment_status is 'paid'
      if (result.status === 'complete' && result.payment_status === 'paid') {
        // Refresh token balance first
        const refreshed = await refreshTokenBalance();
        
        // Set success flag to trigger notifications
        setStripePaymentSuccess(true);
        
        return refreshed;
      } else {
        // Log the reason why we're not considering this payment a success
        if (result.status !== 'complete') {
          console.log(`Payment not successful: session status is ${result.status}, not 'complete'`);
        } else if (result.payment_status !== 'paid') {
          console.log(`Payment not successful: payment_status is ${result.payment_status}, not 'paid'`);
        }
        return false;
      }
    } catch (error) {
      console.error('Unexpected error in handlePaymentSuccess:', error);
      return false;
    }
  };
  
  // Set admin role (enable/disable)
  const setAdminRole = async (enable: boolean, password: string): Promise<boolean> => {
    if (enable) {
      // Enable admin mode - requires password verification
      if (!user.email || !user.token) {
        console.error('Cannot set admin role for unauthenticated user');
        return false;
      }
      
      // Verify admin password
      try {
        const response = await api.auth.verifyAdminPassword(password);
        if (response.success) {
          // Update role to admin
          updateUserState({ role: UserRole.Admin });
          // Store role in localStorage (but don't persist across sessions)
          setLocalStorageItem('userRole', UserRole.Admin);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Admin password verification error:', error);
        return false;
      }
    } else {
      // Disable admin mode - just update role
      updateUserState({ role: UserRole.User });
      // Remove admin role from localStorage
      removeLocalStorageItem('userRole');
      return true;
    }
  };
  
  // Clear Stripe-related data from browser storage
  const clearStripeData = (): void => {
    // Clear Stripe-specific data that might be cached in localStorage
    // These are keys that Stripe commonly uses for caching
    const stripePrefixes = ['__stripe', 'stripe', '__stripe_mid', 'stripe_mid'];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Check if this is a Stripe-related key
        const isStripeKey = stripePrefixes.some(prefix => key.startsWith(prefix));
        if (isStripeKey) {
          localStorage.removeItem(key);
        }
        
        // Also remove any transaction or checkout related items
        if (key.includes('transaction_') || key.includes('checkout') || key.includes('session')) {
          localStorage.removeItem(key);
        }
      }
    }
    
    // Clear any stored purchase or transaction data
    localStorage.removeItem('last_purchase_result');
    localStorage.removeItem('last_purchase_backup');
    
    // Clear all sessionStorage which may contain transaction data
    sessionStorage.clear();
  };

  // Log out user
  const logout = (): void => {
    // Check if we are on the purchase result page
    const isPurchaseResult = window.location.pathname === '/purchase-result';
    
    // Update the user state
    updateUserState({
      email: null,
      token: null,
      role: UserRole.Public,
      tokenBalance: 0
    });
    
    // Explicitly clear userEmail from localStorage and cookie to ensure Stripe doesn't reuse it
    localStorage.removeItem('userEmail');
    document.cookie = 'userEmail=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    // Clear any other user-related data
    localStorage.removeItem('authToken');
    document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    localStorage.removeItem('userRole');
    localStorage.removeItem('preferredUserRole'); // Also clear preferred role on logout
    
    // Clear all Stripe and transaction related data
    clearStripeData();
    
    console.log('User logged out, all data cleared from storage');
    
    // Only redirect if not on the purchase result page
    if (!isPurchaseResult) {
      window.location.href = '/logged-out';
    } else {
      console.log('Logout called on purchase result page - skipping redirect');
    }
  };
  
  // Access control helper for counters
  const hasCounterAccess = (
    ownerEmail: string | null, 
    isOwnerUrl: boolean, 
    accessType: 'view' | 'manage'
  ): boolean => {
    // Admin always has access
    if (isAdmin) {
      return true;
    }
    
    // Owner always has access to their counter
    const isOwner = user.email !== null && ownerEmail === user.email;
    if (isOwner) {
      return true;
    }
    
    // For view access via public URL
    if (accessType === 'view' && !isOwnerUrl) {
      return true;
    }
    
    // For owner URL created by public user (ownerEmail is null), anyone has access
    if (isOwnerUrl && ownerEmail === null) {
      return true;
    }
    
    // In all other cases, deny access
    return false;
  };

  // Listen for localStorage changes in other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === 'authToken' && e.newValue !== getLocalStorageItem('authToken', '')) {
        // Token changed in another tab
        const newToken = e.newValue || null;
        updateUserState({ token: newToken });
      }
      
      if (e.key === 'userEmail' && e.newValue !== getLocalStorageItem('userEmail', '')) {
        // Email changed in another tab
        const newEmail = e.newValue || null;
        updateUserState({ email: newEmail });
      }
      
      if (e.key === 'userRole' && e.newValue !== getLocalStorageItem('userRole', '')) {
        // Role changed in another tab
        const newRole = e.newValue === 'admin' ? UserRole.Admin : 
                        (user.email ? UserRole.User : UserRole.Public);
        updateUserState({ role: newRole });
      }
      
      if (e.key === 'preferredUserRole' && e.newValue !== getLocalStorageItem('preferredUserRole', '')) {
        // Preferred role changed in another tab - update if user is logged in
        if (user.email && user.token) {
          const newRole = e.newValue === 'admin' ? UserRole.Admin : UserRole.User;
          updateUserState({ role: newRole });
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user.email, user.token]);

  // Context value
  const value: UserContextType = {
    user,
    isLoggedIn,
    isAdmin,
    isAuthenticating,
    isTokenOperationLoading,
    stripePaymentSuccess,
    setStripePaymentSuccess,
    login,
    logout,
    verifyToken,
    signup,
    updateEmail,
    setAdminRole,
    addTokens,
    refreshTokenBalance,
    handlePaymentSuccess,
    hasCounterAccess,
    changePassword,
    requestPasswordReset,
    setCountryCode: (countryCode: string | null) => {
      updateUserState({ countryCode });
    }
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

// Hook for using the context
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
} 